import { logout } from "@/store/authSlice";
import {
  createBaseQueryWithReauth,
  HTTP_METHODS,
  INTERVIEW,
  INTERVIEW_ROUTES,
} from "@cointervue/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { invalidateCandidateStatusOnSuccess } from "./candidatesApi";
import type {
  CancelInterviewApiRequest,
  EditPanelistRequest,
  EditPanelistResponse,
  FetchByInterviewIdApiResponse,
  FetchInterviewDetailsApiResponse,
  FetchInterviewsApiRequest,
  FetchInterviewsApiResponse,
  InterviewRecordingRequest,
  InterviewRecordingResponse,
  RescheduledInterviewApiRequest,
  RescheduledInterviewApiResponse,
  ScheduledInterviewApiRequest,
  ScheduledInterviewApiResponse,
} from "./types";

export const candidatesInterviewApi = createApi({
  reducerPath: "candidatesInterviewApi",
  baseQuery: createBaseQueryWithReauth(INTERVIEW.href, logout),
  tagTypes: ["Interview", "Candidates"],

  endpoints: (builder) => ({
    scheduleInterview: builder.mutation<
      ScheduledInterviewApiResponse,
      ScheduledInterviewApiRequest
    >({
      query: (body) => ({
        url: INTERVIEW_ROUTES.scheduleInterview,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "application/json",
        },
        body,
      }),
      invalidatesTags: (_res, err, body) =>
        err
          ? []
          : [
              { type: "Interview", id: "LIST" },
              { type: "Interview" },
              { type: "Candidates", id: "LIST" },
              { type: "Candidates", id: body.candidateId },
            ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    rescheduleInterview: builder.mutation<
      RescheduledInterviewApiResponse,
      RescheduledInterviewApiRequest
    >({
      query: (request) => ({
        url: `${INTERVIEW_ROUTES.reScheduleInterview}/${request.id}`,
        method: HTTP_METHODS.PUT,
        headers: {
          accept: "application/json",
        },
        body: {
          mode: request.mode,
          scheduledDate: request.scheduledDate,
          panelMembers: request.panelMembers,
          instructions: request.instructions,
          notes: request.notes,
          time: request.time,
          candidateTz: request.candidateTz,
        },
      }),
      invalidatesTags: (_res, err, { id }) =>
        err
          ? []
          : [
              { type: "Interview", id: "LIST" },
              { type: "Interview", id },
              { type: "Interview" },
              { type: "Candidates", id: "LIST" },
            ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    fetchInterviewDetails: builder.query<
      FetchInterviewDetailsApiResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${INTERVIEW_ROUTES.fetchInterviewDetails}/${id}`,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Interview", id }],
      keepUnusedDataFor: 60, // Cache for 60 seconds
    }),
    cancelInterview: builder.mutation<
      FetchInterviewDetailsApiResponse,
      CancelInterviewApiRequest
    >({
      query: ({ id, reason, notifyCandidate, notifyPanels }) => ({
        url: `${INTERVIEW_ROUTES.cancelInterview}/${id}`,
        method: HTTP_METHODS.PATCH,
        headers: {
          accept: "application/json",
        },
        body: {
          reason,
          notifyCandidate,
          notifyPanels,
        },
      }),
      invalidatesTags: (_res, err, { id }) =>
        err
          ? []
          : [
              { type: "Interview", id: "LIST" },
              { type: "Interview", id },
              { type: "Interview" },
              { type: "Candidates", id: "LIST" },
            ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    fetchByInterviewId: builder.query<
      FetchByInterviewIdApiResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/${id}`,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Interview", id }],
      keepUnusedDataFor: 60, // Cache for 60 seconds
    }),
    fetchInterview: builder.query<
      FetchInterviewsApiResponse,
      FetchInterviewsApiRequest
    >({
      query: ({ from, to, filter, token = "" }) => ({
        url: "",
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
        },
        params: {
          ...(from && { from }),
          ...(to && { to }),
          ...(filter && { filter }),
          ...(token && { token }),
        },
      }),
      providesTags: [{ type: "Interview", id: "LIST" }],
    }),
    fetchInterviewRecording: builder.query<
      InterviewRecordingResponse,
      InterviewRecordingRequest
    >({
      query: ({ interviewId }) => ({
        url: INTERVIEW_ROUTES.interviewRecording,
        method: HTTP_METHODS.GET,
        params: {
          interviewId,
        },
        headers: {
          accept: "application/json",
        },
      }),
      providesTags: (_result, _error, { interviewId }) => [
        { type: "Interview", id: `RECORDING_${interviewId}` },
      ],
      keepUnusedDataFor: 300,
    }),
    editPanelist: builder.mutation<
      EditPanelistResponse,
      EditPanelistRequest
    >({
      query: ({ interviewId, panelMembers }) => ({
        url: INTERVIEW_ROUTES.editPanelist + interviewId,
        method: HTTP_METHODS.PATCH,

        headers: {
          accept: "application/json",
        },
        body: {panelMembers},
      }),
      invalidatesTags: (_res, err, { interviewId }) =>
        err ? [] : [{ type: "Interview", id: interviewId }],
    }),
  }),
});

export const {
  useScheduleInterviewMutation,
  useRescheduleInterviewMutation,
  useLazyFetchInterviewDetailsQuery,
  useCancelInterviewMutation,
  useLazyFetchByInterviewIdQuery,
  useFetchInterviewQuery,
  useFetchInterviewRecordingQuery,
  useEditPanelistMutation,
} = candidatesInterviewApi;
